import {
	ClientPortfolioInstructionHoldingTypeEnum,
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import {
	getInstructionSchema,
	refineRequiredInstructionFields,
} from "web_myplann/Portfolio/Forms/ToolsShared/instructionSchema";
import { getManualValueShape } from "web_myplann/Portfolio/PortfolioShared/getManualValueShape";
import { toolPreviousValueSchema } from "web_myplann/Portfolio/PortfolioShared/ManualValue/toolPreviousValueSchema";
import { refineManualValue } from "web_myplann/Portfolio/PortfolioShared/refineManualValue";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const IDENTIFICATION_MAX_LENGTH = 50;
const NAME_MAX_LENGTH = 200;

export const getInvestmentInstructionSchema = (t: TranslateFunction) => {
	return getInstructionSchema(t).and(
		z.object({
			count: z
				.number()
				.gt(0, t(Resources.Portfolio.AddShared.Validations.positiveNumber))
				.max(
					ValidationLimits.Amount.Max,
					t(Resources.Portfolio.AddShared.Validations.maxValue),
				)
				.nullish()
				.transform((value) => value ?? null),
		}),
	);
};

export const getInvestmentInstructionFormSchema = (t: TranslateFunction) =>
	getInvestmentInstructionSchema(t).superRefine(
		refineRequiredInstructionFields({
			required: t(Resources.Portfolio.AddShared.Validations.required),
			positiveInteger: t(
				Resources.Portfolio.AddShared.Validations.positiveInteger,
			),
		}),
	);

export const getInvestmentToolSchema = (t: TranslateFunction) =>
	z
		.object({
			id: z.number().nullable(),
			type: z.nativeEnum(ClientPortfolioToolTypeEnum).nullable(),
			identificationType: z
				.nativeEnum(ClientPortfolioToolIdentificationTypeEnum)
				.nullable(),
			identification: trimmedNullable(
				z.string().max(
					IDENTIFICATION_MAX_LENGTH,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: IDENTIFICATION_MAX_LENGTH,
					}),
				),
			),
			holdingType: z
				.nativeEnum(ClientPortfolioInstructionHoldingTypeEnum)
				.nullable(),
			name: trimmedNullable(
				z.string().max(
					NAME_MAX_LENGTH,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: NAME_MAX_LENGTH,
					}),
				),
			),
			...getManualValueShape(t),
			valueId: z.number().nullable(),
			previousValues: z.array(toolPreviousValueSchema),
			isAdvisorBond: z.boolean(),
			instructions: z.array(getInvestmentInstructionSchema(t)),
		})
		.superRefine(refineManualValue(t));

export type InvestmentToolInput = z.input<
	ReturnType<typeof getInvestmentToolSchema>
>;
export type InvestmentToolOutput = z.output<
	ReturnType<typeof getInvestmentToolSchema>
>;
export type InvestmentInstructionInput = z.input<
	ReturnType<typeof getInvestmentInstructionSchema>
>;
export type InvestmentInstructionOutput = z.output<
	ReturnType<typeof getInvestmentInstructionSchema>
>;
